import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { List } from "@phosphor-icons/react";

/**
 * Mobile-only side menu used in dashboard headers.
 *
 * Strictly mobile: render this inside a `md:hidden` container so the desktop /
 * tablet layouts stay untouched. Desktop keeps its inline icon strip.
 *
 * Items shape:
 *   { icon, label, onClick, badge?, testid?, tone?, divider? }
 *   - tone: "default" | "danger" | "success" | "warning"
 *   - divider: true → render a thin separator above this item
 *   - render: fn(close) → custom node (used for things like LinkedDriverCard
 *     that bring their own dialogs/state)
 */
export default function MobileNavMenu({ items = [], title = "Menu", testid = "mobile-menu" }) {
  const [open, setOpen] = useState(false);

  const toneClass = (tone) => {
    switch (tone) {
      case "danger": return "text-red-300";
      case "success": return "text-emerald-300";
      case "warning": return "text-amber-300";
      default: return "text-zinc-200";
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid={`${testid}-trigger`}
          aria-label="Ouvrir le menu"
          className="inline-flex items-center justify-center h-10 w-10 rounded-md text-zinc-300 hover:text-white hover:bg-zinc-800 active:scale-95 transition"
        >
          <List size={22} weight="bold" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[86vw] max-w-sm bg-[#0a0a0a] border-l border-zinc-800 text-white p-0 flex flex-col"
        data-testid={testid}
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-zinc-800">
          <SheetTitle className="text-zinc-100 text-base font-medium">{title}</SheetTitle>
        </SheetHeader>
        <nav className="flex-1 overflow-y-auto py-2">
          {items.map((it, idx) => {
            if (it.render) {
              return (
                <div key={idx} className={`px-4 py-2 ${it.divider ? "mt-2 pt-3 border-t border-zinc-800" : ""}`}>
                  {it.render(() => setOpen(false))}
                </div>
              );
            }
            const Icon = it.icon;
            return (
              <button
                key={idx}
                data-testid={it.testid}
                onClick={() => { setOpen(false); setTimeout(() => it.onClick?.(), 50); }}
                className={`w-full flex items-center gap-3 px-5 py-3.5 text-left text-sm font-medium hover:bg-zinc-900 active:bg-zinc-900/70 transition ${toneClass(it.tone)} ${it.divider ? "mt-1 border-t border-zinc-800" : ""}`}
              >
                {Icon && <Icon size={18} className="shrink-0 opacity-90" />}
                <span className="flex-1 truncate">{it.label}</span>
                {it.badge != null && it.badge !== 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold">
                    {it.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <SheetClose className="hidden" />
      </SheetContent>
    </Sheet>
  );
}
